import api, { ApiResponse } from './api';

// ============================================================
// ATTENDANCE IMPORT TYPES
// ============================================================

export interface AttendanceFileInfo {
  detectedFormat: AttendanceFileFormat;
  separator?: string;
  hasHeader: boolean;
  columns: string[];
  totalRows: number;
  dateFormat?: string;
  columnSuggestions: ColumnSuggestion[];
}

export interface ColumnSuggestion {
  columnName: string;
  columnIndex: number;
  suggestedMapping: string;
  confidence: number;
}

export enum AttendanceFileFormat {
  Auto = 0,
  Excel = 1,
  CSV = 2,
  TXT = 3,
  ZKTeco = 4,
  Bilteks = 5,
  Generic = 6
}

export interface AttendanceImportOptions {
  separator?: string;
  dateFormat?: string;
  hasHeader?: boolean;
  createMissingEmployees?: boolean;
  skipDuplicates?: boolean;
  deviceId?: number;
  employeeCodeColumn?: string;
  employeeNameColumn?: string;
  dateTimeColumn?: string;
  dateColumn?: string;
  timeColumn?: string;
  checkTypeColumn?: string;
}

export interface AttendanceImportResult {
  success: boolean;
  totalRows: number;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  duplicateCount: number;
  errors: string[];
  warnings: string[];
  importedRecords: ImportedAttendance[];
  minDate?: string;
  maxDate?: string;
  uniqueEmployeeCount: number;
}

export interface ImportedAttendance {
  rowNumber: number;
  employeeCode?: string;
  fullName?: string;
  logDateTime?: string;
  checkType?: string;
  status: string;
  errorMessage?: string;
}

// Employee import types
export interface ExcelImportResult {
  success: boolean;
  totalRows: number;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  errors: string[];
  warnings: string[];
  importedEmployees: ImportedEmployee[];
}

export interface ImportedEmployee {
  rowNumber: number;
  employeeCode?: string;
  fullName?: string;
  department?: string;
  status: string;
  errorMessage?: string;
}

const importService = {
  // ============================================================
  // EMPLOYEE IMPORT
  // ============================================================

  previewColumns: async (file: File): Promise<ApiResponse<string[]>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.uploadFile('/import/preview-columns', formData);
  },

  previewEmployeeData: async (file: File, maxRows: number = 10): Promise<ApiResponse<Record<string, any>[]>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.uploadFile(`/import/preview?maxRows=${maxRows}`, formData);
  },

  importEmployeesFromBilteks: async (file: File): Promise<ApiResponse<ExcelImportResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.uploadFile('/import/employees/bilteks', formData);
  },

  // ============================================================
  // ATTENDANCE IMPORT
  // ============================================================

  detectAttendanceFormat: async (file: File): Promise<ApiResponse<AttendanceFileInfo>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.uploadFile('/import/attendance/detect', formData);
  },

  previewAttendance: async (file: File, maxRows: number = 10): Promise<ApiResponse<{
    fileInfo: AttendanceFileInfo;
    preview: Record<string, any>[];
  }>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.uploadFile(`/import/attendance/preview?maxRows=${maxRows}`, formData);
  },

  importAttendance: async (file: File, options: AttendanceImportOptions = {}): Promise<ApiResponse<AttendanceImportResult>> => {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (options.separator) params.append('separator', options.separator);
    if (options.dateFormat) params.append('dateFormat', options.dateFormat);
    if (options.hasHeader !== undefined) params.append('hasHeader', options.hasHeader.toString());
    if (options.createMissingEmployees !== undefined) params.append('createMissingEmployees', options.createMissingEmployees.toString());
    if (options.skipDuplicates !== undefined) params.append('skipDuplicates', options.skipDuplicates.toString());
    if (options.deviceId) params.append('deviceId', options.deviceId.toString());
    if (options.employeeCodeColumn) params.append('employeeCodeColumn', options.employeeCodeColumn);
    if (options.employeeNameColumn) params.append('employeeNameColumn', options.employeeNameColumn);
    if (options.dateTimeColumn) params.append('dateTimeColumn', options.dateTimeColumn);
    if (options.dateColumn) params.append('dateColumn', options.dateColumn);
    if (options.timeColumn) params.append('timeColumn', options.timeColumn);
    if (options.checkTypeColumn) params.append('checkTypeColumn', options.checkTypeColumn);

    const query = params.toString() ? `?${params.toString()}` : '';
    return api.uploadFile(`/import/attendance${query}`, formData);
  },
};

export default importService;
